import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Calendar, Trophy, Shuffle, ListOrdered, Printer, LayoutGrid, List as ListIcon, MapPin, Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import DrawGrid from "@/components/DrawGrid";

export default function Schedule() {
  const { user } = useAuth();
  const [teams, setTeams] = useState([]);
  const [matches, setMatches] = useState([]);
  const [standings, setStandings] = useState([]);
  const [courts, setCourts] = useState("Court 1, Court 2");
  const [selectedTeams, setSelectedTeams] = useState([]);
  const [resetExisting, setResetExisting] = useState(true);
  const [view, setView] = useState("list"); // 'list' | 'grid'
  const [venues, setVenues] = useState([]);
  const [venuesOpen, setVenuesOpen] = useState(false);
  const canManage = ["super_admin", "league_admin"].includes(user.role);

  const load = async () => {
    const [t, m, s, v] = await Promise.all([
      api.get("/teams"), api.get("/matches"), api.get("/standings"),
      api.get("/venues").catch(() => ({ data: [] })),
    ]);
    setTeams(t.data); setMatches(m.data); setStandings(s.data);
    setVenues(v.data || []);
    setSelectedTeams(t.data.map((x) => x.id));
  };
  useEffect(() => { load(); }, []);

  const toggleTeam = (id) => {
    setSelectedTeams((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const genRoundRobin = async () => {
    if (selectedTeams.length < 2) return toast.error("Pick at least 2 teams");
    const pairs = selectedTeams.length * (selectedTeams.length - 1) / 2;
    if (resetExisting && !window.confirm(`⚠ DESTRUCTIVE: This will DELETE all existing scheduled/live group matches before creating ${pairs} new matches across the ${selectedTeams.length} selected teams. Continue?`)) return;
    if (!resetExisting && !window.confirm(`Append ${pairs} new round-robin matches for the ${selectedTeams.length} selected teams. Continue?`)) return;
    try {
      const courtList = courts.split(",").map((c) => c.trim()).filter(Boolean);
      const r = await api.post("/schedule/round-robin", { courts: courtList, team_ids: selectedTeams, reset: resetExisting });
      toast.success(`Created ${r.data.created} matches`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const genKnockout = async () => {
    try {
      const r = await api.post("/schedule/knockout");
      toast.success(`Created ${r.data.created} bracket matches`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const byStage = {
    group: matches.filter((m) => m.stage === "group"),
    quarter: matches.filter((m) => m.stage === "quarter"),
    semi: matches.filter((m) => m.stage === "semi"),
    final: matches.filter((m) => m.stage === "final"),
  };

  // Season 3 §4 — split group ties into A/B/C using each team's `group` field.
  // Aggregates matches by tie_id so admins see "N ties" not "N seed matches".
  const teamGroup = React.useMemo(() => {
    const map = {};
    teams.forEach((t) => { if (t.group) map[t.id] = t.group; });
    return map;
  }, [teams]);
  const groupedByGroup = React.useMemo(() => {
    const out = { A: [], B: [], C: [], unassigned: [] };
    // Aggregate seed matches (same tie_id) into one representative row.
    const seen = new Set();
    byStage.group.forEach((m) => {
      if (m.tie_id) {
        if (seen.has(m.tie_id)) return;
        seen.add(m.tie_id);
      }
      const ga = teamGroup[m.team_a_id];
      const gb = teamGroup[m.team_b_id];
      const g = ga || gb;
      if (g === "A" || g === "B" || g === "C") out[g].push(m);
      else out.unassigned.push(m);
    });
    return out;
  }, [byStage.group, teamGroup]);
  const teamsByGroup = React.useMemo(() => {
    const out = { A: [], B: [], C: [] };
    teams.forEach((t) => { if (t.group && out[t.group]) out[t.group].push(t); });
    return out;
  }, [teams]);

  const generateGroupTies = async (group) => {
    const groupTeams = teamsByGroup[group] || [];
    if (groupTeams.length < 2) return toast.error(`Group ${group} has fewer than 2 teams`);
    const nowIso = new Date().toISOString().slice(0, 16);
    const start = window.prompt(`Start time for Group ${group} ties (YYYY-MM-DDTHH:MM). Ties are staggered by slot minutes:`, nowIso);
    if (!start) return;
    const slot = parseInt(window.prompt("Minutes between ties?", "90") || "90", 10);
    if (!window.confirm(`Generate all round-robin ties for Group ${group} (${groupTeams.length} teams → ${(groupTeams.length * (groupTeams.length - 1)) / 2} ties)?`)) return;
    try {
      const courtList = courts.split(",").map((c) => c.trim()).filter(Boolean);
      const { data } = await api.post("/schedule/group-ties", {
        group, start_at: new Date(start).toISOString(), slot_minutes: slot,
        courts: courtList, reset: false,
      });
      toast.success(`Group ${group}: ${data.ties_created} ties created`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const [newTieFor, setNewTieFor] = useState(null); // group key when open
  const [newTie, setNewTie] = useState({
    team_a_id: "", team_b_id: "",
    // One slot per seed match, in the tenant's tie_match_order.
    // Each slot has its own venue + court + start time.
    slots: [{ venue: "", court: "Court 1", scheduled_at: "" }, { venue: "", court: "Court 2", scheduled_at: "" }, { venue: "", court: "Court 3", scheduled_at: "" }],
  });
  const [playOrder, setPlayOrder] = useState([2, 1, 3]);

  useEffect(() => {
    api.get("/config/tie-match-order").then(({ data }) => setPlayOrder(data?.tie_match_order || [2, 1, 3])).catch(() => {});
  }, []);

  // Consolidated list of all available courts from every venue (deduped).
  // Falls back to the comma-string when no venues are configured yet.
  const allCourtsByVenue = React.useMemo(() => {
    if (venues.length > 0) return venues.map((v) => ({ venue: v.name, courts: v.courts || [] }));
    return [{ venue: "", courts: courts.split(",").map((c) => c.trim()).filter(Boolean) }];
  }, [venues, courts]);

  const openNewTie = (group) => {
    const base = new Date(Date.now() + 60 * 60 * 1000);
    const stagger = (i) => {
      const t = new Date(base.getTime() + i * 90 * 60 * 1000); // default 90-min stagger
      return t.toISOString().slice(0, 16);
    };
    const firstVenue = allCourtsByVenue[0];
    const pickSlot = (i) => {
      const list = firstVenue?.courts || [];
      return {
        venue: firstVenue?.venue || "",
        court: list[i % (list.length || 1)] || `Court ${i + 1}`,
        scheduled_at: stagger(i),
      };
    };
    setNewTie({
      team_a_id: "",
      team_b_id: "",
      slots: [0, 1, 2].map(pickSlot),
    });
    setNewTieFor(group);
  };
  const submitNewTie = async () => {
    if (!newTie.team_a_id || !newTie.team_b_id) return toast.error("Pick both teams");
    if (newTie.team_a_id === newTie.team_b_id) return toast.error("Teams must differ");
    for (let i = 0; i < 3; i++) {
      if (!newTie.slots[i]?.scheduled_at) return toast.error(`Set the start time for Match ${i + 1}`);
      if (!newTie.slots[i]?.court?.trim()) return toast.error(`Set the court for Match ${i + 1}`);
    }
    try {
      await api.post("/ties", {
        team_a_id: newTie.team_a_id, team_b_id: newTie.team_b_id, stage: "group",
        scheduled_at: new Date(newTie.slots[0].scheduled_at).toISOString(),
        scheduled_ats: newTie.slots.map((s) => new Date(s.scheduled_at).toISOString()),
        courts: newTie.slots.map((s) => s.court.trim()),
        venues: newTie.slots.map((s) => s.venue || null),
      });
      toast.success("Tie created");
      setNewTieFor(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const rescheduleMatch = async (matchId, newTime) => {
    try {
      await api.patch(`/matches/${matchId}/schedule`, { scheduled_at: new Date(newTime).toISOString() });
      toast.success("Match rescheduled");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="p-6 md:p-10 max-w-7xl">
      <div className="mb-6 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">Tournament Flow</div>
          <h1 className="font-anton text-5xl md:text-6xl uppercase tracking-tighter text-white">Schedule</h1>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-lg border border-white/10 overflow-hidden" data-testid="view-toggle">
            <button
              onClick={() => setView("list")}
              className={`px-3 py-2 text-xs uppercase tracking-widest flex items-center gap-1.5 ${view === "list" ? "bg-white/10 text-white" : "text-zinc-400 hover:text-white"}`}
              data-testid="view-toggle-list"
            >
              <ListIcon className="w-3.5 h-3.5" /> List
            </button>
            <button
              onClick={() => setView("grid")}
              className={`px-3 py-2 text-xs uppercase tracking-widest flex items-center gap-1.5 ${view === "grid" ? "bg-white/10 text-white" : "text-zinc-400 hover:text-white"}`}
              data-testid="view-toggle-grid"
            >
              <LayoutGrid className="w-3.5 h-3.5" /> Grid
            </button>
          </div>
          <Button
            data-testid="btn-print-draws"
            onClick={async () => {
              try {
                const base = (api.defaults.baseURL || "").replace(/\/api\/?$/, "");
                const url = `${base}/api/exports/tournament-draw.pdf`;
                const resp = await api.get("/exports/tournament-draw.pdf", { responseType: "blob" });
                const href = URL.createObjectURL(resp.data);
                const a = document.createElement("a");
                a.href = href; a.download = "tournament-draw.pdf"; a.click();
                setTimeout(() => URL.revokeObjectURL(href), 5000);
              } catch (e) {
                toast.error("Failed to generate draw PDF");
              }
            }}
            className="bg-[#F4B400] text-black font-bold uppercase tracking-widest hover:bg-[#e5aa00]"
          >
            <Printer className="w-4 h-4 mr-1.5" /> Print Draws
          </Button>
        </div>
      </div>

      {/* Controls */}
      {canManage && (
        <Card className="p-5 bg-[#11241b] border-white/10 mb-6">
          <div className="grid md:grid-cols-[1fr_auto_auto_auto] gap-3 items-end">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-zinc-500">
                Courts (comma-separated) {venues.length > 0 && <span className="text-zinc-600 normal-case">· fallback when no venue picked · {venues.length} venue{venues.length > 1 ? "s" : ""} configured</span>}
              </label>
              <Input data-testid="schedule-courts" value={courts} onChange={(e) => setCourts(e.target.value)} className="bg-[#060d09] border-white/10 text-white mt-1" />
            </div>
            <Button data-testid="btn-manage-venues" onClick={() => setVenuesOpen(true)} variant="outline" className="border-[#F4B400]/40 text-[#F4B400] hover:bg-[#F4B400]/10">
              <MapPin className="w-4 h-4 mr-1.5" /> Manage Venues ({venues.length})
            </Button>
            <Button data-testid="btn-gen-rr" onClick={genRoundRobin} disabled={selectedTeams.length < 2} className="bg-[#00A86B] hover:bg-[#00935A] text-white font-bold">
              <Shuffle className="w-4 h-4 mr-1.5" /> Generate Round-Robin
            </Button>
            <Button data-testid="btn-gen-ko" onClick={genKnockout} variant="outline" className="border-white/20 text-white">
              <Trophy className="w-4 h-4 mr-1.5" /> Generate Knockout (QF→SF→F)
            </Button>
          </div>
          <div className="mt-4">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Teams to include ({selectedTeams.length}/{teams.length})</div>
            <div className="flex flex-wrap gap-1.5">
              {teams.map((t) => (
                <button key={t.id} data-testid={`pick-team-${t.id}`} onClick={() => toggleTeam(t.id)}
                  className={`text-xs px-3 py-1.5 rounded-md border ${selectedTeams.includes(t.id) ? "border-[#00A86B] bg-[#00A86B]/15 text-[#00A86B]" : "border-white/10 bg-[#060d09] text-zinc-400"}`}>
                  {t.name}
                </button>
              ))}
            </div>
          </div>
          <div className="mt-3 flex items-center gap-2">
            <input type="checkbox" id="reset-existing" checked={resetExisting} onChange={(e) => setResetExisting(e.target.checked)} data-testid="reset-existing" />
            <label htmlFor="reset-existing" className="text-xs text-zinc-300">Reset existing scheduled/live group matches before generating</label>
          </div>
          <div className="mt-2 text-xs text-zinc-500">Knockout uses standings from finished group matches (top 8 → QF, top 4 → SF, top 2 → Final)</div>
        </Card>
      )}

      {/* Standings */}
      <Card className="p-5 bg-[#11241b] border-white/10 mb-6">
        <div className="flex items-center gap-2 mb-3">
          <ListOrdered className="w-4 h-4 text-[#00A86B]" />
          <h2 className="font-anton text-2xl uppercase tracking-tight text-white">Standings</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="standings-table">
            <thead className="text-[10px] uppercase tracking-widest text-zinc-500 text-left">
              <tr className="border-b border-white/10"><th className="p-2">#</th><th>Team</th><th className="text-right">P</th><th className="text-right">W</th><th className="text-right">D</th><th className="text-right">L</th><th className="text-right">Diff</th><th className="text-right">Pts</th></tr>
            </thead>
            <tbody>
              {standings.map((s, i) => (
                <tr key={s.team_id} className="border-b border-white/5" data-testid={`standing-${s.team_id}`}>
                  <td className="p-2 text-zinc-500">{i + 1}</td>
                  <td className="text-white font-medium">
                    <Link to={`/teams/${s.team_id}`} className="hover:text-[#F4B400] transition-colors" data-testid={`schedule-standings-team-${s.team_id}`}>{s.team}</Link>
                  </td>
                  <td className="text-right text-zinc-300">{s.played}</td>
                  <td className="text-right text-emerald-400">{s.won}</td>
                  <td className="text-right text-zinc-400">{s.drew}</td>
                  <td className="text-right text-red-400">{s.lost}</td>
                  <td className="text-right text-zinc-300">{s.diff > 0 ? "+" : ""}{s.diff}</td>
                  <td className="text-right font-mono-score text-[#00A86B]">{s.pts}</td>
                </tr>
              ))}
              {standings.length === 0 && <tr><td colSpan={8} className="text-center text-zinc-500 p-6">No standings yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Stage columns */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-3">
          <div className="font-anton text-xl uppercase tracking-tight text-white">Round Robin — by Group</div>
          <span className="text-xs text-zinc-500">{byStage.group.length} ties</span>
        </div>
        {view === "grid" ? (
          <DrawGrid
            matches={matches}
            teams={teams}
            courts={courts.split(",").map((s) => s.trim()).filter(Boolean)}
            onReload={load}
          />
        ) : (
          <>
            <div className="grid md:grid-cols-3 gap-3">
              {["A", "B", "C"].map((g) => (
                <GroupCol
                  key={g}
                  group={g}
                  matches={groupedByGroup[g] || []}
                  teamCount={(teamsByGroup[g] || []).length}
                  canManage={canManage}
                  onGenerate={() => generateGroupTies(g)}
                  onNewTie={() => openNewTie(g)}
                  onReschedule={rescheduleMatch}
                  testid={`stage-group-${g}`}
                />
              ))}
            </div>
            {(groupedByGroup.unassigned || []).length > 0 && (
              <div className="mt-3">
                <GroupCol group="Unassigned" matches={groupedByGroup.unassigned} testid="stage-group-unassigned" />
              </div>
            )}
          </>
        )}
      </div>
      {view === "list" && (
        <div className="grid lg:grid-cols-3 gap-4">
          <StageCol title="Quarter Finals" matches={byStage.quarter} testid="stage-qf" />
          <StageCol title="Semi Finals" matches={byStage.semi} testid="stage-sf" />
          <StageCol title="Final" matches={byStage.final} accent testid="stage-final" />
        </div>
      )}

      {/* Venues manager */}
      {venuesOpen && (
        <VenuesManager
          venues={venues}
          onClose={() => setVenuesOpen(false)}
          onChanged={load}
        />
      )}

      {/* New Tie dialog */}
      {newTieFor && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={() => setNewTieFor(null)}>
          <Card className="p-5 bg-[#0f1a15] border-white/10 max-w-2xl w-full my-8" onClick={(e) => e.stopPropagation()} data-testid="new-tie-dialog">
            <div className="font-anton text-2xl text-white uppercase mb-1">New Team Tie · Group {newTieFor}</div>
            <div className="text-xs text-zinc-500 mb-4">3 seed matches per tie · pick court + time for each · played in the tenant's play order.</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-400 mb-1">Team A</div>
                <select value={newTie.team_a_id} onChange={(e) => setNewTie({ ...newTie, team_a_id: e.target.value })} className="w-full bg-[#060d09] border border-white/10 rounded px-2 py-2 text-white text-sm" data-testid="new-tie-team-a">
                  <option value="">— Pick team A —</option>
                  {(teamsByGroup[newTieFor] || []).map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-400 mb-1">Team B</div>
                <select value={newTie.team_b_id} onChange={(e) => setNewTie({ ...newTie, team_b_id: e.target.value })} className="w-full bg-[#060d09] border border-white/10 rounded px-2 py-2 text-white text-sm" data-testid="new-tie-team-b">
                  <option value="">— Pick team B —</option>
                  {(teamsByGroup[newTieFor] || []).filter(t => t.id !== newTie.team_a_id).map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
            </div>

            <div className="text-[10px] uppercase tracking-widest text-zinc-400 mb-2">
              Seed Matches · played in this order:{" "}
              <span className="text-[#F4B400] font-bold">Seed {playOrder.join(" → Seed ")}</span>
            </div>
            <div className="space-y-2">
              {newTie.slots.map((slot, i) => {
                const seed = playOrder[i] ?? i + 1;
                const activeVenue = allCourtsByVenue.find((v) => v.venue === slot.venue) || allCourtsByVenue[0];
                return (
                  <div key={i} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-center p-3 rounded border border-white/10 bg-[#060d09]" data-testid={`new-tie-slot-${i + 1}`}>
                    <div className="md:col-span-2 flex items-center gap-2">
                      <div className="w-9 h-9 rounded-full bg-[#F4B400]/10 border border-[#F4B400]/40 flex items-center justify-center font-anton text-lg text-[#F4B400]">{i + 1}</div>
                      <div>
                        <div className="text-[9px] uppercase tracking-widest text-zinc-500 leading-tight">Match {i + 1}</div>
                        <div className="text-xs font-bold text-white">Seed {seed}</div>
                      </div>
                    </div>
                    <div className="md:col-span-3">
                      <div className="text-[9px] uppercase tracking-widest text-zinc-500 mb-1">Venue</div>
                      <select
                        value={slot.venue || ""}
                        onChange={(e) => {
                          const v = allCourtsByVenue.find((x) => x.venue === e.target.value);
                          const newCourt = (v?.courts || [])[0] || slot.court;
                          const slots = newTie.slots.map((s, idx) => idx === i ? { ...s, venue: e.target.value, court: newCourt } : s);
                          setNewTie({ ...newTie, slots });
                        }}
                        className="w-full bg-black/40 border border-white/10 rounded px-2 py-2 text-white text-sm"
                        data-testid={`new-tie-slot-${i + 1}-venue`}
                      >
                        {venues.length === 0 ? (
                          <option value="">— No venues configured —</option>
                        ) : (
                          venues.map((v) => <option key={v.id} value={v.name}>{v.name}</option>)
                        )}
                      </select>
                    </div>
                    <div className="md:col-span-3">
                      <div className="text-[9px] uppercase tracking-widest text-zinc-500 mb-1">Court</div>
                      <select
                        value={slot.court}
                        onChange={(e) => {
                          const slots = newTie.slots.map((s, idx) => idx === i ? { ...s, court: e.target.value } : s);
                          setNewTie({ ...newTie, slots });
                        }}
                        className="w-full bg-black/40 border border-white/10 rounded px-2 py-2 text-white text-sm"
                        data-testid={`new-tie-slot-${i + 1}-court`}
                      >
                        {[...new Set([...(activeVenue?.courts || []), slot.court])].filter(Boolean).map((c) => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                      </select>
                    </div>
                    <div className="md:col-span-4">
                      <div className="text-[9px] uppercase tracking-widest text-zinc-500 mb-1">Start time</div>
                      <input
                        type="datetime-local"
                        value={slot.scheduled_at}
                        onChange={(e) => {
                          const slots = newTie.slots.map((s, idx) => idx === i ? { ...s, scheduled_at: e.target.value } : s);
                          setNewTie({ ...newTie, slots });
                        }}
                        className="w-full bg-black/40 border border-white/10 rounded px-2 py-2 text-white text-sm"
                        data-testid={`new-tie-slot-${i + 1}-time`}
                      />
                    </div>
                  </div>
                );
              })}
            </div>

            {venues.length === 0 && (
              <div className="mt-3 text-[11px] text-amber-400 bg-amber-400/10 border border-amber-400/30 rounded px-3 py-2" data-testid="new-tie-no-venues-hint">
                No venues configured yet. Close this dialog and click <b>Manage Venues</b> to add one — you can define up to <b>12 courts per venue</b> and run the tournament across multiple venues.
              </div>
            )}

            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => setNewTieFor(null)} className="text-xs px-3 py-2 rounded border border-white/10 text-zinc-300" data-testid="new-tie-cancel">Cancel</button>
              <button onClick={submitNewTie} className="text-xs px-3 py-2 rounded bg-[#F4B400] text-black font-bold" data-testid="new-tie-submit">Create Tie</button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}

// Split group matches into 3 sub-lists (A/B/C) based on team.group
function GroupCol({ group, matches, teamCount, canManage, onGenerate, onNewTie, onReschedule, testid }) {
  const groupColors = { A: "#F4B400", B: "#00A86B", C: "#7DD3FC", Unassigned: "#94A3B8" };
  const accent = groupColors[group] || "#94A3B8";
  return (
    <div data-testid={testid} className="p-3 rounded-xl bg-[#0f1a15] border border-white/10" style={{ borderTop: `3px solid ${accent}` }}>
      <div className="flex items-center justify-between mb-2 flex-wrap gap-1">
        <div>
          <div className="font-anton text-lg uppercase tracking-tight" style={{ color: accent }}>Group {group}</div>
          {typeof teamCount === "number" && <div className="text-[10px] text-zinc-500 -mt-0.5">{teamCount} teams</div>}
        </div>
        <div className="flex items-center gap-1 flex-wrap">
          <span className="text-xs text-zinc-500">{matches.length} ties</span>
          {canManage && onNewTie && (
            <button
              onClick={onNewTie}
              className="text-[10px] uppercase tracking-widest px-2 py-1 rounded border border-[#00A86B]/40 bg-[#00A86B]/10 text-[#00A86B] hover:bg-[#00A86B]/20"
              data-testid={`btn-new-tie-${group}`}
            >
              + New Tie
            </button>
          )}
          {canManage && onGenerate && (
            <button
              onClick={onGenerate}
              className="text-[10px] uppercase tracking-widest px-2 py-1 rounded border border-white/10 bg-black/30 text-white hover:bg-white/10"
              data-testid={`btn-generate-group-${group}`}
            >
              + Auto-Schedule
            </button>
          )}
        </div>
      </div>
      <div className="space-y-2">
        {matches.map((m) => (
          <TieRow key={m.tie_id || m.id} m={m} canManage={canManage} onReschedule={onReschedule} />
        ))}
        {matches.length === 0 && <div className="text-xs text-zinc-600 text-center py-6">No ties yet — click Auto-Schedule or + New Tie.</div>}
      </div>
    </div>
  );
}

function TieRow({ m, canManage, onReschedule }) {
  const [editing, setEditing] = React.useState(false);
  const [when, setWhen] = React.useState(m.scheduled_at ? m.scheduled_at.slice(0, 16) : "");
  const label = m.scheduled_at
    ? new Date(m.scheduled_at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })
    : "unscheduled";
  return (
    <Card className="p-3 bg-[#11241b] border-white/10" data-testid={`tie-${m.tie_id || m.id}`}>
      <div className="flex items-baseline justify-between">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500">{m.court || "—"}</div>
        {canManage && !editing && (
          <button onClick={() => setEditing(true)} className="text-[10px] text-[#F4B400] hover:underline" data-testid={`edit-time-${m.id}`}>Edit time</button>
        )}
      </div>
      <div className="flex items-baseline justify-between mt-1">
        <Link
          to={`/teams/${m.team_a_id}`}
          className={`font-anton text-sm uppercase truncate hover:text-[#F4B400] transition-colors ${m.winner === "a" ? "text-[#00A86B]" : "text-white"}`}
          data-testid={`tie-team-a-${m.tie_id || m.id}`}
        >{m.team_a?.name}</Link>
        <Link
          to={`/teams/${m.team_b_id}`}
          className={`font-anton text-sm uppercase truncate text-right hover:text-[#F4B400] transition-colors ${m.winner === "b" ? "text-[#00A86B]" : "text-white"}`}
          data-testid={`tie-team-b-${m.tie_id || m.id}`}
        >{m.team_b?.name}</Link>
      </div>
      {editing ? (
        <div className="mt-2 flex items-center gap-1">
          <input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} className="flex-1 text-xs bg-[#060d09] border border-white/10 rounded px-2 py-1 text-white" data-testid={`time-input-${m.id}`} />
          <button onClick={() => { onReschedule(m.id, when); setEditing(false); }} className="text-[10px] px-2 py-1 rounded bg-[#F4B400] text-black font-bold" data-testid={`time-save-${m.id}`}>Save</button>
          <button onClick={() => setEditing(false)} className="text-[10px] px-2 py-1 rounded border border-white/10 text-zinc-400">×</button>
        </div>
      ) : (
        <div className="text-[10px] text-zinc-500 mt-1 flex items-center justify-between">
          <span>🕒 {label}</span>
          <span>{m.status === "live" ? "🔴 LIVE" : m.status}</span>
        </div>
      )}
      {m.tie_id && (
        <div className="mt-1 text-[9px] uppercase tracking-widest text-zinc-600">tie · 3 seed matches</div>
      )}
    </Card>
  );
}

function StageCol({ title, matches, accent, testid }) {
  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <div className={`font-anton text-xl uppercase tracking-tight ${accent ? "text-[#00A86B]" : "text-white"}`}>{title}</div>
        <span className="text-xs text-zinc-500">{matches.length}</span>
      </div>
      <div className="space-y-2">
        {matches.map((m) => (
          <Card key={m.id} className={`p-3 ${accent ? "bg-[#00A86B]/5 border-[#00A86B]/30" : "bg-[#11241b] border-white/10"}`}>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">{m.court}</div>
            <div className="flex items-baseline justify-between mt-1">
              <Link to={`/teams/${m.team_a_id}`} className={`font-anton text-sm uppercase truncate hover:text-[#F4B400] transition-colors ${m.winner === "a" ? "text-[#00A86B]" : "text-white"}`}>{m.team_a?.name}</Link>
              <span className="font-mono-score text-white text-xs">{m.score_a}:{m.score_b}</span>
              <Link to={`/teams/${m.team_b_id}`} className={`font-anton text-sm uppercase truncate text-right hover:text-[#F4B400] transition-colors ${m.winner === "b" ? "text-[#00A86B]" : "text-white"}`}>{m.team_b?.name}</Link>
            </div>
            <div className="text-[10px] text-zinc-500 mt-1">{m.status === "live" ? "🔴 LIVE" : m.status}</div>
          </Card>
        ))}
        {matches.length === 0 && <div className="text-xs text-zinc-600 text-center py-6">—</div>}
      </div>
    </div>
  );
}


function VenuesManager({ venues, onClose, onChanged }) {
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [courts, setCourts] = useState(["Court 1"]);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null); // venue.id when editing

  const startEdit = (v) => {
    setEditing(v.id);
    setName(v.name);
    setAddress(v.address || "");
    setCourts([...(v.courts || ["Court 1"])]);
  };
  const resetForm = () => {
    setEditing(null);
    setName("");
    setAddress("");
    setCourts(["Court 1"]);
  };

  const save = async () => {
    if (!name.trim()) return toast.error("Venue name required");
    const clean = courts.map((c) => c.trim()).filter(Boolean);
    if (clean.length === 0) return toast.error("Add at least one court");
    if (clean.length > 12) return toast.error("Max 12 courts per venue");
    setBusy(true);
    try {
      const body = { name: name.trim(), address: address.trim() || null, courts: clean };
      if (editing) {
        await api.put(`/venues/${editing}`, body);
        toast.success(`Updated ${body.name}`);
      } else {
        await api.post("/venues", body);
        toast.success(`Added ${body.name}`);
      }
      resetForm();
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const removeVenue = async (v) => {
    if (!window.confirm(`Delete venue "${v.name}"? This does NOT delete matches; existing matches keep their court label but lose the venue link.`)) return;
    setBusy(true);
    try {
      await api.delete(`/venues/${v.id}`);
      toast.success(`Deleted ${v.name}`);
      if (editing === v.id) resetForm();
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const setCourtAt = (i, val) => setCourts(courts.map((c, idx) => idx === i ? val : c));
  const addCourt = () => {
    if (courts.length >= 12) return toast.error("Max 12 courts per venue");
    setCourts([...courts, `Court ${courts.length + 1}`]);
  };
  const removeCourt = (i) => setCourts(courts.filter((_, idx) => idx !== i));

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <Card className="p-5 bg-[#0f1a15] border-white/10 max-w-3xl w-full my-8" onClick={(e) => e.stopPropagation()} data-testid="venues-manager">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="font-anton text-2xl text-white uppercase">Venues</div>
            <div className="text-xs text-zinc-500">Run the event across multiple venues · up to 12 courts each</div>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white" data-testid="venues-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Existing list */}
        {venues.length > 0 && (
          <div className="mb-5">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Configured venues</div>
            <div className="grid md:grid-cols-2 gap-2">
              {venues.map((v) => (
                <div key={v.id} className={`p-3 rounded border ${editing === v.id ? "border-[#F4B400] bg-[#F4B400]/5" : "border-white/10 bg-black/30"}`} data-testid={`venue-row-${v.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-bold text-white text-sm truncate">{v.name}</div>
                      {v.address && <div className="text-[11px] text-zinc-500 truncate">{v.address}</div>}
                      <div className="text-[10px] text-zinc-500 mt-1">
                        <span className="text-[#F4B400] font-bold">{(v.courts || []).length}</span> courts · {(v.courts || []).join(", ")}
                      </div>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <button onClick={() => startEdit(v)} className="text-[10px] px-2 py-1 rounded border border-white/10 text-zinc-300 hover:border-white/30" data-testid={`venue-edit-${v.id}`}>Edit</button>
                      <button onClick={() => removeVenue(v)} className="text-[10px] px-2 py-1 rounded border border-red-500/30 text-red-400 hover:bg-red-500/10" data-testid={`venue-delete-${v.id}`}>
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Editor */}
        <div className="p-4 rounded border border-white/10 bg-[#060d09]" data-testid="venue-editor">
          <div className="text-[10px] uppercase tracking-widest text-[#F4B400] mb-3">{editing ? "Edit venue" : "Add new venue"}</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
            <div>
              <label className="text-[9px] uppercase tracking-widest text-zinc-500">Venue name</label>
              <Input value={name} onChange={(e) => setName(e.target.value)} className="bg-black/40 border-white/10 text-white mt-1" placeholder="Sky Padel Club" data-testid="venue-name-input" />
            </div>
            <div>
              <label className="text-[9px] uppercase tracking-widest text-zinc-500">Address / area (optional)</label>
              <Input value={address} onChange={(e) => setAddress(e.target.value)} className="bg-black/40 border-white/10 text-white mt-1" placeholder="Bandra, Mumbai" data-testid="venue-address-input" />
            </div>
          </div>
          <label className="text-[9px] uppercase tracking-widest text-zinc-500">Courts ({courts.length} / 12)</label>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-2">
            {courts.map((c, i) => (
              <div key={i} className="flex items-center gap-1" data-testid={`venue-court-row-${i + 1}`}>
                <Input value={c} onChange={(e) => setCourtAt(i, e.target.value)} className="bg-black/40 border-white/10 text-white text-sm" />
                {courts.length > 1 && (
                  <button onClick={() => removeCourt(i)} className="text-red-400 p-1 hover:bg-red-500/10 rounded" data-testid={`venue-court-remove-${i + 1}`}>
                    <X className="w-3 h-3" />
                  </button>
                )}
              </div>
            ))}
          </div>
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <button onClick={addCourt} disabled={courts.length >= 12} className="text-xs px-3 py-1.5 rounded border border-white/20 text-zinc-300 hover:border-[#F4B400] hover:text-[#F4B400] disabled:opacity-40" data-testid="venue-add-court">
              <Plus className="inline w-3 h-3 mr-1" /> Add court
            </button>
            <button onClick={() => setCourts(Array.from({ length: 12 }, (_, i) => `Court ${i + 1}`))} className="text-xs px-3 py-1.5 rounded border border-white/20 text-zinc-300 hover:border-white/40" data-testid="venue-fill-12">
              Fill 12 courts
            </button>
            <div className="flex-1" />
            {editing && (
              <button onClick={resetForm} className="text-xs px-3 py-1.5 rounded border border-white/10 text-zinc-400" data-testid="venue-cancel-edit">Cancel edit</button>
            )}
            <Button onClick={save} disabled={busy} className="bg-[#F4B400] text-black font-bold uppercase tracking-widest" data-testid="venue-save">
              {busy ? "Saving…" : editing ? "Save changes" : "Add Venue"}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
